import { createAsyncThunk } from "@reduxjs/toolkit"

import type {
    IOVBulkUpsertPayerMappingsRequestData,
    IOVGetPayerMappingsRequestData,
    IOVUpsertPayerMappingRequestData,
} from "@common-shared/types/requestData/oneVerifyRequestData.types"

import { PAYER_MAPPINGS_CONFIG } from "../../config/payerMappings.config"
import { CsvHelper } from "../../helpers/csv.helper"
import { PayerMappingsCsvHelper } from "../../helpers/payerMappingsCsv.helper"
import { UtilHelper } from "../../helpers/util.helper"
import { PayerMappingsService } from "../../services/payerMappings.service"
import type { IThunkActionWithPracticeData } from "../../types/general.types"
import {
    payerMappingsDeleteDone,
    payerMappingsSetExportProgress,
    payerMappingsSetList,
    payerMappingsUpdateDone,
} from "../slices/payerMappings.slice"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"

/**
 * Get paginated list of payer mappings
 */
export const payerMappingsGetList = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IOVGetPayerMappingsRequestData | undefined>
>("payerMappingsGetListPrefix", async (action, { dispatch }) => {
    if (!action.ignorePreloader) {
        dispatch(runtimeStartLoading("payerMappingsGetListLoading"))
    }

    try {
        const pagination = action.payload || UtilHelper.getPagination()

        const isUnfiltered = !(
            action.payload?.externalName ||
            action.payload?.externalPayerId ||
            action.payload?.payerId
        )

        const result = await PayerMappingsService.getList(action.practice, {
            start: pagination.start,
            count: pagination.count,
            externalName: action.payload?.externalName,
            externalPayerId: action.payload?.externalPayerId,
            payerId: action.payload?.payerId,
        })

        if (result) {
            dispatch(
                payerMappingsSetList({
                    items: result.items,
                    newPagination: result.pagination,
                    isUnfiltered,
                }),
            )

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        if (!action.ignorePreloader) {
            dispatch(runtimeStopLoading("payerMappingsGetListLoading"))
        }

        action.onFinally?.()
    }
})

/**
 * Create payer mapping
 */
export const payerMappingsCreate = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IOVUpsertPayerMappingRequestData>
>("payerMappingsCreatePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("payerMappingsCreateLoading"))

    try {
        const result = await PayerMappingsService.createInstance(
            action.practice,
            action.payload,
        )

        if (result) {
            // Rely on the parent component to trigger a list refresh

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("payerMappingsCreateLoading"))

        action.onFinally?.()
    }
})

/**
 * Update payer mapping
 */
export const payerMappingsUpdate = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        payerMappingId: number
        data: IOVUpsertPayerMappingRequestData
    }>
>("payerMappingsUpdatePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("payerMappingsUpdateLoading"))

    try {
        const result = await PayerMappingsService.updateInstance(
            action.practice,
            action.payload.payerMappingId,
            action.payload.data,
        )

        if (result) {
            dispatch(payerMappingsUpdateDone(result))

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("payerMappingsUpdateLoading"))

        action.onFinally?.()
    }
})

/**
 * Delete payer mapping
 */
export const payerMappingsDelete = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<number>
>("payerMappingsDeletePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("payerMappingsDeleteLoading"))

    try {
        const result = await PayerMappingsService.deleteInstance(
            action.practice,
            action.payload,
        )

        if (result) {
            dispatch(payerMappingsDeleteDone())

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("payerMappingsDeleteLoading"))

        action.onFinally?.()
    }
})

/**
 * Validate bulk payer mappings upsert without writing
 */
export const payerMappingsValidateBulk = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IOVBulkUpsertPayerMappingsRequestData>
>("payerMappingsValidateBulkPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("payerMappingsValidateBulkLoading"))

    try {
        const result = await PayerMappingsService.validateBulk(
            action.practice,
            action.payload,
        )

        if (!result) {
            throw new Error("Failed to validate payer mappings.")
        }

        // The pattern we're using is to rely on the parent to use the results directly rather than putting them into the store
        action.onSuccess?.(result)
    } catch (e) {
        action.onError?.()
    } finally {
        dispatch(runtimeStopLoading("payerMappingsValidateBulkLoading"))

        action.onFinally?.()
    }
})

/**
 * Bulk upsert payer mappings
 */
export const payerMappingsUpsertBulk = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IOVBulkUpsertPayerMappingsRequestData>
>("payerMappingsUpsertBulkPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("payerMappingsUpsertBulkLoading"))

    try {
        const result = await PayerMappingsService.upsertBulk(
            action.practice,
            action.payload,
        )

        if (!result) {
            throw new Error("Failed to upsert payer mappings.")
        }

        // We're relying on the review component to unmount and the table to remount and refresh the list
        action.onSuccess?.(result)
    } catch (e) {
        action.onError?.()
    } finally {
        dispatch(runtimeStopLoading("payerMappingsUpsertBulkLoading"))

        action.onFinally?.()
    }
})

/**
 * Export all payer mappings as CSV (paged at API max).
 */
export const payerMappingsExportList = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<undefined>
>("payerMappingsExportListPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("payerMappingsExportListLoading"))
    dispatch(payerMappingsSetExportProgress(0))

    try {
        await CsvHelper.fetchPagesAndDownloadCsv({
            pageSize: PAYER_MAPPINGS_CONFIG.csvExportPageSize,
            fetchPage: pagination =>
                PayerMappingsService.getList(action.practice, {
                    start: pagination.start,
                    count: pagination.count,
                }),
            headers: Object.keys(PAYER_MAPPINGS_CONFIG.csvHeaderToKey),
            mapRow: PayerMappingsCsvHelper.mappingToCsvRow,
            fileName: PAYER_MAPPINGS_CONFIG.csvExportBaseFileName,
            onProgress: progress => {
                dispatch(payerMappingsSetExportProgress(progress))
            },
        })

        action.onSuccess?.()
    } catch (e) {
        action.onError?.()
    } finally {
        dispatch(runtimeStopLoading("payerMappingsExportListLoading"))
        dispatch(payerMappingsSetExportProgress(0))
        action.onFinally?.()
    }
})
